import { useContext, useEffect, useRef } from 'react';
import { useExperiment } from '@printdeal/feature-management/react';
import { VatModeContext } from '@printdeal/product-selector';
import { useSegmentation } from '@printdeal/segmentation/react';
import { useAccount } from '../../../../hooks/useAccount';
import { isSiteNl } from '../../../../helpers/env';
import { getFromLocalStorage, LS_KEYS } from '../../../../helpers/window';
import { isRecognizedUser } from '../../../../templates/homepage/homepage-helper';
import {
  getDefaultVatModeForVisitor,
  hasExistingVatPreference,
  VAT_TOGGLE_EXPERIMENT_ID,
} from './vatToggleHelpers';

/**
 * Variant-only: align the initial VAT mode with the visitor type when nothing
 * else (Google Shopping `?vat=` or a stored preference) already decided it.
 * Runs once so a later toggle is not overwritten by the automatic default.
 */
export const useVatTogglePreference = () => {
  const { isInitialized, experiment } = useExperiment(VAT_TOGGLE_EXPERIMENT_ID);
  const { vatMode, vatModeFromUrl, setVatMode } = useContext(VatModeContext);
  const segmentationData = useSegmentation();
  const { account } = useAccount();
  const hasResolvedPreference = useRef(false);

  const isVariant = isInitialized && experiment?.variation === 'variant';

  useEffect(() => {
    if (!isSiteNl() || !isVariant || !segmentationData.hasData || hasResolvedPreference.current) {
      return;
    }

    hasResolvedPreference.current = true;

    if (hasExistingVatPreference({
      vatModeFromUrl,
      storedVatMode: getFromLocalStorage(LS_KEYS.VAT_MODE),
    })) {
      return;
    }

    const preferredVatMode = getDefaultVatModeForVisitor({
      isRecognized: isRecognizedUser(account?.id),
      isEnduser: Boolean(segmentationData.isEndUser()),
      isReseller: Boolean(segmentationData.isReseller()),
    });

    if (preferredVatMode !== vatMode) {
      setVatMode(preferredVatMode);
    }
  }, [
    account?.id,
    segmentationData,
    isVariant,
    setVatMode,
    vatMode,
    vatModeFromUrl,
  ]);
};
